import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MdAdd, MdMoreVert, MdEdit, MdDelete, MdVisibility, MdSearch } from 'react-icons/md';
import axios from 'axios';
import Swal from 'sweetalert2';
import { GET_ALL_INWARD_API, DELETE_INWARD_API } from '../config/API';
import Loader from '../components/common/Loader';
import NoData from '../components/common/NoData';
import ActionMenuPortal from '../components/common/ActionMenuPortal';
import Pagination from '../components/common/Pagination';
import { useUser } from '../context/UserContext';
import './InwardManagement.css';
import { formatDate } from '../utils/dateUtils';

const InwardManagement = () => {
    const { hasPermission } = useUser();
    const [inwards, setInwards] = useState([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const limit = 10;

    // Action Menu State
    const [openMenuId, setOpenMenuId] = useState(null);
    const [anchorRect, setAnchorRect] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const fetchInwards = async (page = 1) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(GET_ALL_INWARD_API, {
                params: {
                    page: page,
                    limit: limit,
                    search: debouncedSearch || undefined
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setInwards(response.data.data || []);
                const pagination = response.data.pagination || {};
                setTotalPages(pagination.totalPages || response.data.totalPages || 1);
                setTotalRecords(pagination.total || pagination.totalRecords || response.data.totalRecords || 0);
                setCurrentPage(response.data.currentPage || page);
            }
        } catch (error) {
            console.error('Error fetching inwards:', error);
            // showErrorToast('Failed to fetch inward records.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInwards(currentPage);
    }, [currentPage, debouncedSearch]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const toggleMenu = (id, e) => {
        e.stopPropagation();
        if (openMenuId === id) {
            setOpenMenuId(null);
            setAnchorRect(null);
        } else {
            setOpenMenuId(id);
            setAnchorRect(e.currentTarget.getBoundingClientRect());
        }
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (event.target.closest('.action-portal-wrapper')) return;
            if (event.target.closest('.action-menu-btn')) return;
            setOpenMenuId(null);
            setAnchorRect(null);
        };
        document.addEventListener('click', handleClickOutside, true);
        return () => document.removeEventListener('click', handleClickOutside, true);
    }, []);

    const handleDelete = async (id) => {
        setOpenMenuId(null);
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This inward record will be permanently deleted!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('accessToken');
                const response = await axios.delete(DELETE_INWARD_API(id), {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire('Deleted!', 'Inward record has been deleted.', 'success');

                    // Optimistic update
                    setInwards(prev => prev.filter(item => item.id !== id));

                    // If it was the last item on the page and not on page 1, go to previous page
                    if (inwards.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        fetchInwards(currentPage);
                    }
                } else {
                    Swal.fire('Error', response.data.error || 'Failed to delete record.', 'error');
                }
            } catch (error) {
                console.error('Delete error:', error);
                Swal.fire('Error', 'Failed to delete inward record.', 'error');
            }
        }
    };

    return (
        <div className="inward-management-container">
            <div className="inward-header">
                <h2>Inward Management</h2>
                {hasPermission('INWARD_MANAGEMENT', 'create') && (
                    <Link to="/inward/entry" className="add-inward-btn">
                        <MdAdd size={20} /> Add Inward
                    </Link>
                )}
            </div>

            <div className="inward-card">
                {/* Search Bar */}
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                    <div style={{ position: 'relative', maxWidth: '500px' }}>
                        <MdSearch
                            style={{
                                position: 'absolute',
                                left: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#9ca3af',
                                fontSize: '1.25rem'
                            }}
                        />
                        <input
                            type="text"
                            placeholder="Search inward records..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '8px',
                                fontSize: '0.875rem',
                                outline: 'none',
                                transition: 'border-color 0.2s',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
                            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: '#e5e7eb',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    color: '#4b5563',
                                    fontSize: '12px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                </div>
                <div className="inward-table-wrapper">
                    <table className="inward-table">
                        <thead>
                            <tr>
                                <th>Inward No</th>
                                <th>Vendor</th>
                                <th>Project</th>
                                <th>PO Number</th>
                                <th>Date</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="6"><Loader text="Loading..." /></td></tr>
                            ) : inwards.length === 0 ? (
                                <tr><td colSpan="6"><NoData text="No inward records found." /></td></tr>
                            ) : (
                                inwards.map((item) => (
                                    <tr key={item.id}>
                                        <td><span className="inward-no" style={{ fontFamily: 'monospace' }}>{item.inwardNo || `#${item.id}`}</span></td>
                                        <td>
                                            <strong>{item.Vendor?.companyName + " " + '(' + (item.Vendor?.vendorName || 'N/A') + ')' || item.vendorName || 'N/A'}</strong>
                                        </td>
                                        <td>
                                            <span className="project-text">{item.ProjectMaster?.projectName || item.projectName || 'N/A'}</span>
                                        </td>
                                        <td>#{item.PurchaseOrder?.poNumber || item.purchaseOrderId || 'N/A'}</td>
                                        <td>{item.date ? formatDate(item.date) : formatDate(item.createdAt)}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button
                                                className="action-menu-btn"
                                                onClick={(e) => toggleMenu(item.id, e)}
                                            >
                                                <MdMoreVert size={20} />
                                            </button>

                                            <ActionMenuPortal
                                                isOpen={openMenuId === item.id}
                                                anchorRect={anchorRect}
                                                onClose={() => setOpenMenuId(null)}
                                            >
                                                <div className="action-dropdown shadow-lg">
                                                    {hasPermission('INWARD_MANAGEMENT', 'read') && (
                                                        <button className="action-item" onClick={() => navigate(`/inward/view/${item.id}`)}>
                                                            <MdVisibility size={16} /> View Inward
                                                        </button>
                                                    )}
                                                    {hasPermission('INWARD_MANAGEMENT', 'update') && (
                                                        <button className="action-item" onClick={() => navigate(`/inward/entry/${item.id}`)}>
                                                            <MdEdit size={16} /> Edit Inward
                                                        </button>
                                                    )}
                                                    {hasPermission('INWARD_MANAGEMENT', 'delete') && (
                                                        <button className="action-item delete-item" onClick={() => handleDelete(item.id)}>
                                                            <MdDelete size={16} /> Delete Inward
                                                        </button>
                                                    )}
                                                </div>
                                            </ActionMenuPortal>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Mobile View */}
                <div className="mobile-inward-view">
                    {loading ? (
                        <Loader />
                    ) : inwards.length === 0 ? (
                        <NoData text="No records found." />
                    ) : (
                        inwards.map((item) => (
                            <div key={item.id} className="mobile-inward-card">
                                <div className="mobile-card-header">
                                    <div className="vendor-project-info">
                                        <span className="inward-no" style={{ fontFamily: 'monospace' }} >{item.inwardNo || `#${item.id}`}</span>
                                        <strong>{item.Vendor?.companyName + " " + '(' + (item.Vendor?.vendorName || 'N/A') + ')' || item.vendorName || 'N/A'}</strong>
                                    </div>
                                    <button className="action-menu-btn" onClick={(e) => toggleMenu(item.id, e)}>
                                        <MdMoreVert size={20} />
                                    </button>
                                </div>
                                <div className="mobile-card-body">
                                    <div className="mobile-row">
                                        <span className="mobile-label">PO:</span>
                                        <span className="mobile-value">#{item.PurchaseOrder?.poNumber || item.purchaseOrderId || 'N/A'}</span>
                                    </div>
                                    <div className="mobile-row">
                                        <span className="mobile-label">Project:</span>
                                        <span className="mobile-value">{item.ProjectMaster?.projectName || item.projectName || 'N/A'}</span>
                                    </div>
                                    <div className="mobile-row">
                                        <span className="mobile-label">Date:</span>
                                        <span className="mobile-value">{formatDate(item.date)}</span>
                                    </div>
                                </div>
                                <ActionMenuPortal
                                    isOpen={openMenuId === item.id}
                                    anchorRect={anchorRect}
                                    onClose={() => setOpenMenuId(null)}
                                >
                                    <div className="action-dropdown shadow-lg">
                                        {hasPermission('INWARD_MANAGEMENT', 'read') && (
                                            <button className="action-item" onClick={() => navigate(`/inward/view/${item.id}`)}>
                                                <MdVisibility size={16} /> View Inward
                                            </button>
                                        )}
                                        {hasPermission('INWARD_MANAGEMENT', 'update') && (
                                            <button className="action-item" onClick={() => navigate(`/inward/entry/${item.id}`)}>
                                                <MdEdit size={16} /> Edit Inward
                                            </button>
                                        )}
                                        {hasPermission('INWARD_MANAGEMENT', 'delete') && (
                                            <button className="action-item delete-item" onClick={() => handleDelete(item.id)}>
                                                <MdDelete size={16} /> Delete Inward
                                            </button>
                                        )}
                                    </div>
                                </ActionMenuPortal>
                            </div>
                        ))
                    )}
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    totalRecords={totalRecords}
                    currentCount={inwards.length}
                    entityName="Inward Records"
                />
            </div>
        </div>
    );
};

export default InwardManagement;
